import {
  compose,
  curry,
  equals,
  join,
  min,
  multiply,
  prop,
  propOr,
} from 'ramda';
import {
  deleteKey as deleteKeyQuery,
  getKey as getKeyQuery,
  setKey as setKeyQuery,
  deleteBasicAuthKey as deleteBasicAuthKeyQuery,
  getBasicAuthKey as getBasicAuthKeyQuery,
  setBasicAuthKey as setBasicAuthKeyQuery,
} from '../queries';
import {
  discardNewKey,
  removeKey,
  updateKey,
  updateNewKey,
} from './sync';
import { KeyState } from '../models';

const getKeyName = compose(join('/'), prop('path'));

const power = curry((x, y) => x ** y);

const waitMultiplierMilliseconds = 1000;

const maxWaitTimeMilleseconds = 20 * 1000;

const calculateWaitDuration = compose(
  min(maxWaitTimeMilleseconds),
  multiply(waitMultiplierMilliseconds),
  power(2),
);

export const deleteKey = curry(async (dispatch, client, domainOrToken, key) => {
  const deleteKeyFn = (key.basicAuth) ? deleteBasicAuthKeyQuery : deleteKeyQuery;
  const keyName = getKeyName(key);
  const queryParams = (key.basicAuth) ? {
    token: domainOrToken,
    key: keyName,
  } : {
    domain: domainOrToken,
    key: keyName,
  };

  dispatch(updateKey(domainOrToken, key.set({ state: KeyState.SYNCHRONIZING })));
  try {
    await deleteKeyFn(client, queryParams);
    dispatch(removeKey(domainOrToken, prop('path', key)));
  } catch (error) {
    dispatch(updateKey(domainOrToken, key.set({ state: KeyState.ERROR, error })));
  }
});

const pullKey = curry(async (client, domainOrToken, key) => {
  const getKeyFn = (key.basicAuth) ? getBasicAuthKeyQuery : getKeyQuery;
  const queryParams = (key.basicAuth) ? {
    token: domainOrToken,
    key: getKeyName(key),
  } : {
    domain: domainOrToken,
    key: getKeyName(key),
  };
  const updatedKey = await getKeyFn(client, queryParams);

  return key.set({ ...updatedKey, state: KeyState.CONSISTENT });
});

const pushKey = curry(async (client, domainOrToken, key) => {
  const setKeyFn = (key.basicAuth) ? setBasicAuthKeyQuery : setKeyQuery;
  const queryParams = (key.basicAuth) ? {
    token: domainOrToken,
    key: getKeyName(key),
    data: prop('data', key),
  } : {
    domain: domainOrToken,
    key: getKeyName(key),
    data: prop('data', key),
    readers: propOr([], 'readers', key),
    writers: propOr([], 'writers', key),
  };
  await setKeyFn(client, queryParams);

  return key.set({ state: KeyState.CONSISTENT });
});

export const synchronizeKey = curry(async (dispatch, client, domainOrToken, key, attempts = 0) => {
  dispatch(updateKey(domainOrToken, key.set({ state: KeyState.SYNCHRONIZING })));

  try {
    switch (key.state) {
      case KeyState.EMPTY: {
        const synchronizedKey = await pullKey(client, domainOrToken, key);
        dispatch(updateKey(domainOrToken, synchronizedKey));
        break;
      }
      case KeyState.CONSISTENT: {
        dispatch(updateKey(domainOrToken, key.set({ state: KeyState.CONSISTENT })));
        break;
      }
      case KeyState.SYNCHRONIZING: {
        throw new Error('Another update is pending - please wait');
      }
      case KeyState.MODIFIED: {
        const synchronizedKey = await pushKey(client, domainOrToken, key);
        dispatch(updateKey(domainOrToken, synchronizedKey));
        break;
      }
      default: {
        dispatch(updateKey(domainOrToken, key));
      }
    }
  } catch (error) {
    setTimeout(
      () => synchronizeKey(dispatch, client, domainOrToken, key, attempts + 1),
      calculateWaitDuration(attempts),
    );
    const userFriendlyError = new Error('Unable to fulfill request, changes not saved');
    dispatch(updateKey(domainOrToken, key.set({
      state: KeyState.MODIFIED,
      error: userFriendlyError,
    })));
  }
});

export const saveNewKey = curry(async (dispatch, client, domainOrToken, key, attempts = 0) => {
  try {
    key.validate();
  } catch (validationError) {
    dispatch(updateNewKey(domainOrToken, key.set({ error: validationError })));
    return;
  }
  dispatch(updateNewKey(domainOrToken, key.set({ state: KeyState.SYNCHRONIZING })));

  try {
    if (equals(key.state, KeyState.MODIFIED)) {
      const synchronizedKey = await pushKey(client, domainOrToken, key);
      dispatch(discardNewKey(domainOrToken));
      dispatch(updateKey(domainOrToken, synchronizedKey.set({ isSelected: false })));
    } else {
      throw new Error('Can not create a new key without user data');
    }
  } catch (error) {
    setTimeout(
      () => saveNewKey(dispatch, client, domainOrToken, key, attempts + 1),
      calculateWaitDuration(attempts),
    );
    const userFriendlyError = new Error('Unable to fulfill request, changes not saved');
    dispatch(updateKey(domainOrToken, key.set({
      state: KeyState.MODIFIED,
      error: userFriendlyError,
    })));
  }
});
